'use client';
import { MouseEvent as ReactMouseEvent, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import { useSearchParams } from 'next/navigation';

import { getDocumentById } from '@/api/documents';
import { checkCoupon, checkEmail } from '@/api/payments';
import Button from '@/components/elements/button';
import { Checkbox } from '@/components/elements/checkbox';
import Input2 from '@/components/elements/input2';
import PaymentOption from '@/components/event-payment/payment-option';
import { paymentQueryParamKeys, PaymentQueryParams } from '@/components/event-payment/types';
import { useToggle } from '@/hooks/use-toggle';
import { useTranslation } from '@/i18n/client';
import { setTickets } from '@/store/event-payment/event-payment.actions';
import { selectSelectedTickets } from '@/store/event-payment/event-payment.selectotrs';
import { Document } from '@/types/documents';
import { EventDetails } from '@/types/events';
import { currencyDefaultCode } from '@/types/general/base';
import { WithLocale } from '@/types/platform-data';
import { PaymentProvider, PaymentProviderId, TicketsBookInput, TicketSelectOption } from '@/types/tickets';
import { documentIds, regexPaterns, storageKeys } from '@/utils/constants';
import { getDocUrl } from '@/utils/file-asset-utils';
import { priceToFixed } from '@/utils/number-utils';

interface Props {
    event: EventDetails;
    paymentProviders: PaymentProvider[];
    onSlideBack: () => void;
    onSubmit: (formData: TicketsBookInput) => Promise<void>;
    isVisible: boolean;
}

export default function SubmitForm(props: WithLocale<Props>) {
    const { t: tCommon } = useTranslation(props.lang, 'common');
    const { t: tPayments } = useTranslation(props.lang, 'payments');

    const selectedTickets = useSelector(selectSelectedTickets);

    const { watch, formState, handleSubmit, setError, clearErrors, setValue } = useFormContext<TicketsBookInput>();

    // todo: these should be handled by the form itself
    const { handleToggle: handleTermsToggle, toggleValue: termsToggleValue } = useToggle(false);
    const [agreementError, setAgreementError] = useState<string | null>(null);
    const [providerError, setProviderError] = useState<string | null>(null);

    const [couponLoading, setCouponLoading] = useState(false);
    const [couponDiscount, setCouponDiscount] = useState<number | null>(0);

    const dispatch = useDispatch();

    const [document, setDocument] = useState<Document>();

    const searchParams = useSearchParams();

    // set form data by query params
    useEffect(() => {
        const queryParams: Partial<Record<keyof PaymentQueryParams, string | null>> = {};

        for (const key of paymentQueryParamKeys) {
            queryParams[key] = searchParams.get(key);
        }

        if (queryParams.customer_email) {
            setValue('customer_email', queryParams.customer_email);
            setValue('customer_email_repeat', queryParams.customer_email);
        }

        queryParams.customer_first_name && setValue('customer_first_name', queryParams.customer_first_name);
        queryParams.customer_second_name && setValue('customer_second_name', queryParams.customer_second_name);
        queryParams.customer_address && setValue('customer_address', queryParams.customer_address);
        queryParams.customer_phone && setValue('customer_phone', queryParams.customer_phone.replace(/\s/g, '+'));

        if (queryParams.tickets) {
            try {
                const queryTickets = decodeURIComponent(queryParams.tickets);
                const parsed = JSON.parse(queryTickets);
                dispatch(setTickets(parsed));
            } catch (error) {
                console.error('Failed to parse tickets:', error);
            }
        }

        if (queryParams.couponCode) {
            setValue('coupon_code', queryParams.couponCode);
            validateCoupon(queryParams.couponCode);
        }

        if (queryParams.payment_provider) {
            setValue('payment_provider', queryParams.payment_provider as PaymentProviderId);
        }

        if (queryParams.customer_telegram) {
            setValue('customer_telegram', queryParams.customer_telegram);
        }
    }, [dispatch]);

    useEffect(() => {
        const fetchDocument = async () => {
            const doc = await getDocumentById(documentIds.terms_of_use_sr);
            if (doc) {
                setDocument(doc);
            }
        };

        fetchDocument();
    }, [props.lang]);

    const getTicketTranslation = (item: TicketSelectOption) => {
        const desc = item.description?.replace(/-/g, ' ').split(' ') ?? [];
        if (desc.length === 0) {
            return item.tag;
        }

        let translate = '';
        for (const word of desc) {
            const translationKey = `seat_schema.${word}`;
            // @ts-ignore
            const translation = tPayments(translationKey);
            translate += translation !== translationKey ? `${translation} ` : `${word} `;
        }
        return translate;
    };

    const orderItems = selectedTickets.map((ticket) => ({
        tag: getTicketTranslation(ticket),
        quantity: ticket.quantity,
        price: String(ticket.price * ticket.quantity * (couponDiscount ? 1 - couponDiscount : 1)) || '',
    }));

    const submitBtnDisabled =
        !termsToggleValue ||
        watch([
            'customer_email',
            'customer_email_repeat',
            'customer_first_name',
            'customer_second_name',
            'payment_provider',
        ]).some((item) => !item) ||
        Object.values(formState.errors).some((item) => item);

    const validateCoupon = async (couponCode: string) => {
        const response = await checkCoupon(props.event.id, couponCode);
        setCouponDiscount(response.discount ? response.discount / 100 : null);
    };

    const handleApplyCoupon = async (e: ReactMouseEvent<Element>): Promise<void> => {
        e.preventDefault();
        setCouponLoading(true);
        try {
            await validateCoupon(watch('coupon_code') || '');
        } finally {
            setCouponLoading(false);
        }
    };

    const paymentCurrency = () =>
        props.paymentProviders.find((prov) => prov.id === watch('payment_provider'))?.currency_code ||
        currencyDefaultCode;

    const validateEmail = async () => {
        const response = await checkEmail(encodeURI(watch('customer_email').toString()));
        if (!response) {
            setError('customer_email', { message: tPayments('form.errors.invalidEmail') });
            return false;
        } else {
            clearErrors('customer_email');
            return true;
        }
    };

    const compareEmail = () => {
        if (
            watch('customer_email').toString().toLocaleLowerCase() !==
            watch('customer_email_repeat').toString().toLocaleLowerCase()
        ) {
            setError('customer_email_repeat', { message: tPayments('form.errors.invalidEmailRepeat') });
            return false;
        } else {
            clearErrors('customer_email_repeat');
            return true;
        }
    };

    const validateAgreement = () => {
        if (!termsToggleValue) {
            setAgreementError(tPayments('form.errors.requiredAgreement'));
            return false;
        } else {
            setAgreementError(null);
            return true;
        }
    };

    const validateProvider = (providerId?: string) => {
        const paymentProvider = props.paymentProviders.find(
            (prov) => prov.id === providerId || prov.id === watch('payment_provider'),
        );
        if (!paymentProvider) {
            setProviderError(tPayments('form.errors.requiredProvider'));
            return null;
        } else {
            setProviderError(null);
            return paymentProvider;
        }
    };

    const onSubmit = async (formData: TicketsBookInput) => {
        if (!validateAgreement()) return;

        const paymentProvider = validateProvider();
        if (!paymentProvider) return;

        // here the "cheaper" validation comes first
        if (!compareEmail()) return;
        if (!(await validateEmail())) return;

        const ticketsBookInput = {
            ...formData,
            event_id: props.event.id,
            coupon_code: couponDiscount ? watch('coupon_code') : undefined,
            country_code: paymentProvider.country_code,
            // todo
            //  pass provider after finishing backend
            payment_provider: 'msu',
            language: props.lang,
            body: selectedTickets.map((ticket) => ({ tag: ticket.tag, quantity: ticket.quantity })),
        };

        window.sessionStorage.setItem(
            storageKeys.eventPaymentOrder,
            JSON.stringify({
                email: formData.customer_email,
                telegram: formData.customer_telegram,
                order: orderItems,
                discount: couponDiscount,
                couponCode: formData.coupon_code,
                event_id: props.event.id,
                tickets: JSON.stringify(selectedTickets),
                //todo remove after finishing backend
                paymentProvider: paymentProvider,
            }),
        );

        // todo
        //  handle booking request correctly
        // const rubPaymentLink = buildUrl('events', {
        //     lang: props.lang,
        //     children: [props.event.slug || props.event.id, 'payment-rub'],
        // });
        // @ts-ignore
        await props.onSubmit(ticketsBookInput);
        // paymentProvider.id === 'tbank' && router.push(rubPaymentLink);
    };

    return (
        <div className="flex min-w-full justify-center pb-6">
            <form onSubmit={handleSubmit(onSubmit)} className="flex w-[40rem] flex-col gap-4 px-6 sm:px-0">
                {/* form-container */}
                <div className="flex flex-col gap-3">
                    <div className="flex flex-col gap-3 sm:flex-row">
                        <Input2
                            name="customer_first_name"
                            label={tCommon('inputs.title.first_name') + ' *'}
                            options={{
                                required: { value: true, message: tPayments('form.errors.requiredName') },
                                onBlur: (e) =>
                                    e.target.value.length === 0
                                        ? setError('customer_first_name', {
                                              message: tPayments('form.errors.requiredName'),
                                          })
                                        : clearErrors('customer_first_name'),
                                onChange: (e) => {
                                    e.target.value = e.target.value
                                        .trim()
                                        .replace(/[^A-Za-zА-я]/g, '')
                                        .slice(0, 34);
                                    e.target.value.length === 0
                                        ? setError('customer_first_name', {
                                              message: tPayments('form.errors.requiredName'),
                                          })
                                        : clearErrors('customer_first_name');
                                },
                            }}
                            withError={Boolean(formState?.errors?.customer_first_name)}
                            errText={formState?.errors?.customer_first_name?.message}
                        />
                        <Input2
                            name="customer_second_name"
                            label={tCommon('inputs.title.second_name') + ' *'}
                            options={{
                                required: { value: true, message: tPayments('form.errors.requiredSecondName') },
                                onBlur: (e) =>
                                    e.target.value.length === 0
                                        ? setError('customer_second_name', {
                                              message: tPayments('form.errors.requiredSecondName'),
                                          })
                                        : clearErrors('customer_second_name'),
                                onChange: (e) => {
                                    e.target.value = e.target.value
                                        .trimStart()
                                        .replace(regexPaterns.sanitizeText, '')
                                        .slice(0, 34);
                                    e.target.value.length === 0
                                        ? setError('customer_second_name', {
                                              message: tPayments('form.errors.requiredSecondName'),
                                          })
                                        : clearErrors('customer_second_name');
                                },
                            }}
                            withError={Boolean(formState?.errors?.customer_second_name)}
                            errText={formState?.errors?.customer_second_name?.message}
                        />
                    </div>
                    <div className="flex flex-col gap-3 sm:flex-row">
                        <Input2
                            name="customer_email"
                            label={tCommon('inputs.title.email') + ' *'}
                            placeholder="me@example.com"
                            options={{
                                required: { value: true, message: tPayments('form.errors.requiredEmail') },
                                onBlur: async () => validateEmail(),
                                onChange: async (e) => {
                                    e.target.value = e.target.value.trim().replace(regexPaterns.sanitizeEmail, '');
                                    await validateEmail();
                                },
                            }}
                            withError={Boolean(formState?.errors?.customer_email)}
                            errText={formState?.errors?.customer_email?.message}
                        />
                        <Input2
                            name="customer_email_repeat"
                            label={tCommon('inputs.title.email_repeat') + ' *'}
                            options={{
                                required: { value: true, message: tPayments('form.errors.requiredEmailRepeat') },
                                onBlur: () => compareEmail(),
                                onChange: (e) => {
                                    e.target.value = e.target.value.trim().replace(regexPaterns.sanitizeEmail, '');
                                    compareEmail();
                                },
                            }}
                            onPaste={(e) => e.preventDefault()}
                            withError={Boolean(formState?.errors?.customer_email_repeat)}
                            errText={formState?.errors?.customer_email_repeat?.message}
                        />
                    </div>
                    <div className="flex flex-col gap-3 sm:flex-row">
                        <Input2
                            name="customer_address"
                            label={tCommon('inputs.title.address')}
                            options={{
                                onChange: (e) =>
                                    (e.target.value = e.target.value
                                        .trimStart()
                                        .replace(regexPaterns.sanitizeTextAndNumbers, '')),
                            }}
                            placeholder="Beograd, Kneza Miloša 27"
                        />
                    </div>
                    <div className="flex flex-col gap-3 sm:flex-row">
                        <Input2
                            name="customer_phone"
                            label={tCommon('inputs.title.phone')}
                            placeholder="+381123456789"
                            options={{
                                onChange: (e) =>
                                    (e.target.value = e.target.value.replace(/(?!^\+)\+/g, '').replace(/[^\d+]/g, '')),
                                validate: (value) =>
                                    value.length === 0 ||
                                    regexPaterns.phone.test(value) ||
                                    tCommon('inputs.validation.phone'),
                            }}
                            withError={Boolean(formState?.errors?.customer_phone)}
                        />
                        <Input2
                            name="customer_telegram"
                            label="Telegram"
                            options={{
                                onChange: (e) =>
                                    (e.target.value = e.target.value
                                        .trim()
                                        .replace(regexPaterns.sanitizeSocialLink, '')),
                            }}
                            placeholder="@polakohedonist"
                        />
                    </div>
                    {props.event.with_promocode && (
                        <div className="flex flex-row gap-3">
                            <Input2
                                name="coupon_code"
                                label={tPayments('common.promo_code')}
                                withSuccess={Boolean(couponDiscount)}
                                withError={couponDiscount === null}
                                errText={tPayments('form.errors.invalidPromoCode')}
                            />
                            <div className="px-4 pt-[1.625rem] sm:pt-[1.875rem]">
                                <button
                                    className={`text-md my-[0.421875rem] border-none bg-none font-bold sm:my-[0.6875rem] ${Boolean(watch('coupon_code')) ? 'cursor-pointer text-accent' : 'cursor-default text-gray-400'}`}
                                    onClick={handleApplyCoupon}
                                    disabled={!Boolean(watch('coupon_code')) || couponLoading}
                                    type="button"
                                >
                                    {tCommon('common.submit')}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* payment-container */}
                <div className="flex flex-col gap-3 pt-4 sm:flex-row">
                    {/* payment-options */}
                    <div className="flex flex-1 flex-col gap-1.5">
                        {props.paymentProviders.map((provider) => (
                            <PaymentOption key={provider.id} name="payment_provider" provider={provider} />
                        ))}
                        {providerError && <span className="text-xs text-red-600">{providerError}</span>}
                    </div>
                    {/* cart-details */}
                    <div className="flex flex-1 flex-col gap-4 md:pl-3">
                        {/* order-wrapper */}
                        <div className="flex flex-col items-end gap-1">
                            <span className="text-md mr-auto font-bold">{tPayments('tickets.orderDetails1')}</span>
                            {orderItems.map((item) => (
                                <div key={item.tag} className="flex min-w-full justify-between gap-1.5">
                                    <span>
                                        {item.quantity > 1
                                            ? tPayments('tickets.itemNameQty1', {
                                                  tag: item.tag,
                                                  quantity: item.quantity,
                                              })
                                            : tPayments('tickets.itemName1', { tag: item.tag })}
                                    </span>
                                    <span>
                                        {tCommon('price.regular', { currency: paymentCurrency(), context: item.price })}
                                    </span>
                                </div>
                            ))}
                            {couponDiscount ? (
                                <div className="flex min-w-full justify-between gap-1.5">
                                    <span>{tPayments('tickets.discount1')}</span>
                                    <span>{couponDiscount * 100}%</span>
                                </div>
                            ) : null}
                        </div>
                        {/* total-wrapper */}
                        <div className="flex flex-col items-end gap-1.5">
                            <div className="flex min-w-full justify-between gap-1 font-medium">
                                <span className="font-bold">{tPayments('tickets.total1')}</span>
                                <span className="font-bold">
                                    {tCommon('price.regular', {
                                        currency: paymentCurrency(),
                                        context: priceToFixed(orderItems.reduce((acc, item) => +acc + +item.price, 0)),
                                    })}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                {/* terms-wrapper */}
                <div className="flex flex-col gap-1">
                    <Checkbox
                        id="agreement"
                        name="agreement"
                        label={tPayments('tickets.agreement', {
                            returnObjects: true,
                            link: document && getDocUrl(document),
                        })}
                        onChange={handleTermsToggle}
                        align="items-baseline"
                        textCls="text-xs"
                    />
                    {agreementError && <span className="text-xs text-red-600">{agreementError}</span>}
                </div>
                {/* button */}
                <Button
                    disabled={submitBtnDisabled}
                    type="submit"
                    title={tCommon('common.pay')}
                    onSubmit={handleSubmit(onSubmit)}
                    loading={formState.isSubmitting}
                />
            </form>
        </div>
    );
}
